/**
 * PersonaCampoRepository - Personas externas del campo (web Dexie + mobile SQLite).
 * Soft-delete vía sync, igual que el resto de las tablas con is_deleted.
 */

import { v4 as uuidv4 } from 'uuid';
import { IPersonaCampoRepository, PersonaCampo } from '../interfaces/IPersonaCampoRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

export class PersonaCampoRepository extends BaseRepository<PersonaCampo, string> implements IPersonaCampoRepository {
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'personas_campo', 'personas_campo');
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;
    if (profileRepo) this.setProfileRepository(profileRepo);
  }

  async add(data: Omit<PersonaCampo, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();
    const record: PersonaCampo = {
      ...data,
      id,
      created_by: data.created_by || profile?.user_id || '',
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      created_at: data.created_at || now,
      updated_at: now,
      is_deleted: false,
    };
    await this.db.insert('personas_campo', record);
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<PersonaCampo> & { id: string }): Promise<void> {
    const updatedData = { ...data, updated_at: this.getCurrentTimestamp() };
    await this.db.update('personas_campo', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  async delete(id: string): Promise<void> {
    await this.softDelete(id);
  }

  async getByEmpresa(empresaId: string): Promise<PersonaCampo[]> {
    const rows = await this.db.queryMultiple<PersonaCampo>('personas_campo', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId },
    ], { column: 'nombre', direction: 'asc' });
    return rows.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }
}
